import {FieldType, useAllFieldsQuery, useAllTagsQuery} from '@transactions/graphql';
import Fuse from 'fuse.js';
import {CornerDownLeft, Hash, ListTree, Type, X} from 'lucide-react';
import * as React from 'react';
import {ScreenList, ScreenRow, ScreenSection} from 'src/CreateSheet/parts';
import {FieldPill} from 'src/inputs/FieldInput/FieldPill';
import {TextFieldPill} from 'src/inputs/FieldInput/TextFieldPill';
import {TagDisplay} from 'src/inputs/TagInput/TagDisplay';
import {TextInput} from 'src/inputs/TextInput';
import {cn} from 'src/lib/utils';
import {Popover} from 'src/Popover/NewPopover';
import {PopoverContents, PopoverItem} from 'src/Popover/views';
import {compareTagsNewestFirst} from 'src/tagOrdering';
import type {FocusControls} from 'src/FocusTrap';

type TagResult = {kind: 'tag'; name: string};
type FieldResult = {
  kind: 'field';
  fieldId: string;
  fieldName: string;
  value: string;
  fieldType: FieldType;
};
type AttachmentResult = {kind: 'attachment'; candidate: AttachmentCandidate};
/** An enum field to browse into: picking it narrows the search to its values. */
type FieldBrowseResult = {kind: 'fieldBrowse'; fieldName: string};
type SearchResult = TagResult | FieldResult | AttachmentResult | FieldBrowseResult;

type SelectableItem =
  | {type: 'result'; result: SearchResult}
  | {type: 'createTag'; name: string};

const resultKey = (result: SearchResult) =>
  result.kind === 'tag'
    ? `tag:${result.name}`
    : result.kind === 'attachment'
      ? `attachment:${result.candidate.key}`
      : result.kind === 'fieldBrowse'
        ? `browse:${result.fieldName}`
        : `field:${result.fieldId}:${result.value || '__text__'}`;

// With an empty search, the screen layout lists at most this many tags.
const BROWSE_LIMIT = 60;

/**
 * A contextual entry offered through the strip's type-ahead alongside tags
 * and fields (e.g. the create modal's benefits). `searchText` is what the
 * query is fuzzy-matched against; `row` is the popover row rendered for it.
 */
export type AttachmentCandidate = {
  key: string;
  searchText: string;
  row: React.ReactNode;
  onAttach: () => void;
};

type Props = {
  tags: string[];
  fieldValues: Record<string, string>;
  onChange: (tags: string[], fieldValues: Record<string, string>) => void;
  focusControlsRef?: React.MutableRefObject<FocusControls | null>;
  // Contextual attachments from the owning form (the create modal's benefit
  // and fee period). Pills render inline among the tag/field pills; blocks
  // render full-width below the input row (for attachments with their own
  // sub-form); candidates join the type-ahead results. Callers without
  // attachments render exactly the strip they always had.
  attachmentPills?: React.ReactNode;
  attachmentBlocks?: React.ReactNode;
  attachmentCandidates?: AttachmentCandidate[];
  /**
   * `inline` is a field among others on a form: one box of pills with a
   * type-ahead popover. `screen` has the space to itself (the create sheet's
   * tags screen): a search box, what's applied under it, and the matches as
   * a list in place, browsable before typing.
   */
  variant?: 'inline' | 'screen';
};

export const TagsAndFieldsInput = ({
  tags,
  fieldValues,
  onChange,
  focusControlsRef,
  attachmentPills,
  attachmentBlocks,
  attachmentCandidates,
  variant = 'inline',
}: Props) => {
  const isScreen = variant === 'screen';
  const {data: tagsData} = useAllTagsQuery();
  const {data: fieldsData} = useAllFieldsQuery();

  const [query, setQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const activeItemRef = React.useRef<HTMLDivElement | null>(null);

  const [editingTextFieldId, setEditingTextFieldId] = React.useState<string | null>(null);

  const allTags = tagsData?.allTags ?? [];
  const fields = fieldsData?.allFields ?? [];

  const enumFields = React.useMemo(
    () => fields.filter((f) => f.type === FieldType.Enum),
    [fields],
  );
  const textFields = React.useMemo(
    () => fields.filter((f) => f.type === FieldType.Text),
    [fields],
  );

  const tagFuse = React.useMemo(
    () => new Fuse(allTags, {keys: ['name'], threshold: 0.4, includeScore: true}),
    [allTags],
  );
  const fieldFuse = React.useMemo(
    () => new Fuse(fields, {keys: ['name'], threshold: 0.4}),
    [fields],
  );
  const textFieldFuse = React.useMemo(
    () => new Fuse(textFields, {keys: ['name'], threshold: 0.4}),
    [textFields],
  );
  const candidateFuse = React.useMemo(
    () =>
      new Fuse(attachmentCandidates ?? [], {
        keys: ['searchText'],
        threshold: 0.4,
      }),
    [attachmentCandidates],
  );

  const allValueItems = React.useMemo(
    () =>
      enumFields.flatMap((f) =>
        f.values.map((val) => ({
          fieldId: f._id,
          fieldName: f.name,
          value: val.value,
          fieldType: FieldType.Enum as const,
        })),
      ),
    [enumFields],
  );
  const valueFuse = React.useMemo(
    () => new Fuse(allValueItems, {keys: ['value'], threshold: 0.4}),
    [allValueItems],
  );
  const perFieldValueFuse = React.useMemo(
    () =>
      Object.fromEntries(
        fields.map((f) => [
          f._id,
          new Fuse(f.values, {keys: ['value'], threshold: 0.4}),
        ]),
      ),
    [fields],
  );

  const searchResults = React.useMemo((): SearchResult[] => {
    if (!query.trim()) {
      if (!isScreen) return [];
      // Nothing typed yet: every tag not already applied, newest first, then
      // the fields not yet set.
      const browseTags: SearchResult[] = allTags
        .filter((t) => !tags.includes(t.name))
        .sort(compareTagsNewestFirst)
        .slice(0, BROWSE_LIMIT)
        .map((t) => ({kind: 'tag', name: t.name}));
      const browseFields: SearchResult[] = fields
        .filter((f) => !(f._id in fieldValues))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((f) =>
          f.type === FieldType.Text
            ? {kind: 'field', fieldId: f._id, fieldName: f.name, value: '', fieldType: FieldType.Text}
            : {kind: 'fieldBrowse', fieldName: f.name},
        );
      return [...browseTags, ...browseFields];
    }

    const colonIndex = query.indexOf(':');

    if (colonIndex === -1) {
      const keyQuery = query.trim();
      const results: SearchResult[] = [];
      const fieldSeen = new Set<string>();

      // Tags first (up to 5), newest year first among equal matches
      const tagMatches = tagFuse
        .search(keyQuery)
        .sort(
          (a, b) =>
            (a.score ?? 0) - (b.score ?? 0) ||
            compareTagsNewestFirst(a.item, b.item),
        )
        .map((r) => r.item);
      let tagCount = 0;
      for (const tag of tagMatches) {
        if (tags.includes(tag.name)) continue;
        if (tagCount >= 5) break;
        results.push({kind: 'tag', name: tag.name});
        tagCount++;
      }

      // Enum fields by name
      const nameMatches = fieldFuse
        .search(keyQuery)
        .map((r) => r.item)
        .filter((f) => f.type === FieldType.Enum);
      for (const f of nameMatches) {
        if (f._id in fieldValues) continue;
        for (const val of f.values) {
          const key = `${f._id}:${val.value}`;
          if (fieldSeen.has(key)) continue;
          fieldSeen.add(key);
          results.push({kind: 'field', fieldId: f._id, fieldName: f.name, value: val.value, fieldType: FieldType.Enum});
        }
      }

      // Enum fields by value
      for (const match of valueFuse.search(keyQuery)) {
        if (match.item.fieldId in fieldValues) continue;
        const key = `${match.item.fieldId}:${match.item.value}`;
        if (fieldSeen.has(key)) continue;
        fieldSeen.add(key);
        results.push({kind: 'field', ...match.item});
      }

      // Text fields by name
      const textMatches = textFieldFuse.search(keyQuery).map((r) => r.item);
      for (const tf of textMatches) {
        if (tf._id in fieldValues) continue;
        results.push({kind: 'field', fieldId: tf._id, fieldName: tf.name, value: '', fieldType: FieldType.Text});
      }

      // Contextual attachments last — they carry richer rows, so they read
      // as a distinct group at the bottom of the list
      for (const match of candidateFuse.search(keyQuery)) {
        results.push({kind: 'attachment', candidate: match.item});
      }

      return results.slice(0, 15);
    } else {
      // Colon path: field:value search only
      const keyQuery = query.slice(0, colonIndex).trim();
      const valueQuery = query.slice(colonIndex + 1).trim();

      const matchedFields = keyQuery
        ? fieldFuse
            .search(keyQuery)
            .map((r) => r.item)
            .filter((f) => f.type === FieldType.Enum)
        : enumFields;

      const results: SearchResult[] = [];
      for (const f of matchedFields) {
        if (f._id in fieldValues) continue;
        let matchedValues = f.values;
        if (valueQuery) {
          const fuse = perFieldValueFuse[f._id];
          matchedValues = fuse ? fuse.search(valueQuery).map((r) => r.item) : [];
        }
        for (const val of matchedValues) {
          results.push({kind: 'field', fieldId: f._id, fieldName: f.name, value: val.value, fieldType: FieldType.Enum});
        }
      }
      return results.slice(0, 15);
    }
  }, [query, isScreen, allTags, tags, fields, enumFields, fieldValues, tagFuse, fieldFuse, textFieldFuse, valueFuse, perFieldValueFuse, candidateFuse]);

  const showCreateTag = React.useMemo(() => {
    const trimmed = query.trim();
    if (!trimmed || trimmed.includes(':')) return false;
    const lower = trimmed.toLowerCase();
    return (
      !allTags.some((t) => t.name.toLowerCase() === lower) &&
      !tags.some((t) => t.toLowerCase() === lower)
    );
  }, [query, allTags, tags]);

  const selectableItems = React.useMemo((): SelectableItem[] => {
    const items: SelectableItem[] = searchResults.map((r) => ({type: 'result' as const, result: r}));
    if (showCreateTag) items.push({type: 'createTag', name: query.trim()});
    return items;
  }, [searchResults, showCreateTag, query]);

  React.useEffect(() => {
    setSelectedIndex(null);
  }, [searchResults]);

  React.useLayoutEffect(() => {
    activeItemRef.current?.scrollIntoView({block: 'nearest'});
  }, [selectedIndex]);

  React.useLayoutEffect(() => {
    if (isOpen && containerRef.current && popoverRef.current) {
      popoverRef.current.style.width = `${Math.max(containerRef.current.offsetWidth, 250)}px`;
    }
  }, [isOpen]);

  const clearQuery = () => {
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(null);
  };

  const handleSelectResult = (result: SearchResult) => {
    if (result.kind === 'tag') {
      onChange([...tags, result.name], fieldValues);
      clearQuery();
      inputRef.current?.focus();
    } else if (result.kind === 'attachment') {
      result.candidate.onAttach();
      clearQuery();
      inputRef.current?.focus();
    } else if (result.kind === 'fieldBrowse') {
      // The colon search lists just this field's values.
      setQuery(`${result.fieldName}:`);
      setIsOpen(true);
      inputRef.current?.focus();
    } else if (result.fieldType === FieldType.Text) {
      onChange(tags, {...fieldValues, [result.fieldId]: ''});
      setEditingTextFieldId(result.fieldId);
      clearQuery();
    } else {
      onChange(tags, {...fieldValues, [result.fieldId]: result.value});
      clearQuery();
      inputRef.current?.focus();
    }
  };

  const handleSelectItem = (item: SelectableItem) => {
    if (item.type === 'result') {
      handleSelectResult(item.result);
    } else {
      onChange([...tags, item.name], fieldValues);
      clearQuery();
      inputRef.current?.focus();
    }
  };

  const handleSelectAtIndex = (index: number | null) => {
    const item = selectableItems[index ?? 0];
    if (item) handleSelectItem(item);
  };

  const handleRemoveTag = (index: number) => {
    onChange([...tags.slice(0, index), ...tags.slice(index + 1)], fieldValues);
  };

  const handleRemoveField = (fieldId: string) => {
    const newValues = {...fieldValues};
    delete newValues[fieldId];
    onChange(tags, newValues);
    if (editingTextFieldId === fieldId) setEditingTextFieldId(null);
  };

  const handleTextFieldCommit = (fieldId: string, value: string) => {
    if (value.trim()) {
      onChange(tags, {...fieldValues, [fieldId]: value.trim()});
    } else {
      const newValues = {...fieldValues};
      delete newValues[fieldId];
      onChange(tags, newValues);
    }
    setEditingTextFieldId(null);
    inputRef.current?.focus();
  };

  const handleInputChange = (value: string) => {
    setQuery(value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    if (query) setIsOpen(true);
  };

  // The screen layout's list is always showing; the inline popover isn't.
  const listOpen = isOpen || isScreen;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!listOpen && query) {
        setIsOpen(true);
      } else if (selectableItems.length > 0) {
        setSelectedIndex((prev) =>
          prev === null ? 0 : Math.min(prev + 1, selectableItems.length - 1),
        );
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (selectableItems.length > 0 && selectedIndex !== null) {
        setSelectedIndex((prev) => (prev === 0 ? null : (prev ?? 0) - 1));
      }
    } else if (e.key === 'Enter') {
      // On the screen layout an empty search lists everything, so Enter
      // picks a row only once one is highlighted or the search is narrowed.
      if (listOpen && selectableItems.length > 0 && (query.trim() !== '' || selectedIndex !== null)) {
        e.preventDefault();
        handleSelectAtIndex(selectedIndex);
      }
    } else if (e.key === 'Tab') {
      // Empty query (or nothing to pick): let Tab move focus past the input.
      if (!query || !listOpen || selectableItems.length === 0) {
        setIsOpen(false);
        return;
      }
      e.preventDefault();
      handleSelectAtIndex(selectedIndex);
      if (e.shiftKey) {
        focusControlsRef?.current?.focusBackward();
      } else {
        focusControlsRef?.current?.focusForward();
      }
    } else if (e.key === 'Backspace' && !query && tags.length > 0) {
      onChange(tags.slice(0, tags.length - 1), fieldValues);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setQuery('');
      setIsOpen(false);
      setSelectedIndex(null);
    }
  };

  const fieldEntries = Object.entries(fieldValues).map(([fieldId, value]) => {
    const field = fields.find((f) => f._id === fieldId);
    return {
      fieldId,
      fieldName: field?.name ?? fieldId,
      fieldType: field?.type ?? FieldType.Enum,
      value,
    };
  });

  // Applied values only — attachment slots may hold ghost "add" chips, which
  // are affordances rather than values and shouldn't shorten the prompt.
  const hasOwnPills = tags.length > 0 || fieldEntries.length > 0;

  const showPopover = isOpen && selectableItems.length > 0;

  const valuePills = (
    <>
      {tags.map((tag, i) =>
        isScreen ? (
          // The inline pill's remove button shows on hover, which a phone
          // never has: here it's always showing.
          <span
            key={tag}
            className="bg-muted-foreground text-background inline-flex h-6 shrink-0 items-center gap-1 rounded-full pr-1 pl-2.5 text-xs"
          >
            {tag}
            <button
              type="button"
              aria-label={`Remove ${tag}`}
              onClick={() => handleRemoveTag(i)}
              className="hover:bg-background/20 cursor-pointer rounded-full p-0.5"
            >
              <X className="size-3" />
            </button>
          </span>
        ) : (
          <TagDisplay key={tag} name={tag} onRemove={() => handleRemoveTag(i)} showRemove />
        ),
      )}
      {fieldEntries.map((entry) =>
        entry.fieldType === FieldType.Text ? (
          <TextFieldPill
            key={entry.fieldId}
            fieldName={entry.fieldName}
            value={entry.value}
            isEditing={editingTextFieldId === entry.fieldId}
            onCommit={(value) => handleTextFieldCommit(entry.fieldId, value)}
            onStartEdit={() => setEditingTextFieldId(entry.fieldId)}
            onRemove={() => handleRemoveField(entry.fieldId)}
          />
        ) : (
          <FieldPill
            key={entry.fieldId}
            fieldName={entry.fieldName}
            value={entry.value}
            onRemove={() => handleRemoveField(entry.fieldId)}
          />
        ),
      )}
      {attachmentPills}
    </>
  );

  if (isScreen) {
    // The keyboard's row: shaded like a hover, without the check that marks
    // a chosen value on the other screens.
    const highlight = (isSelected: boolean) => (isSelected ? '[&>button]:bg-muted' : undefined);
    const resultRow = (item: SelectableItem, index: number) => {
      const isSelected = selectedIndex === index;
      const ref = isSelected ? activeItemRef : null;
      if (item.type === 'createTag') {
        return (
          <div ref={ref} key="create-tag" className={highlight(isSelected)}>
            <ScreenRow
              onClick={() => handleSelectItem(item)}
              leading={<CornerDownLeft className="text-muted-foreground size-4" />}
              label={`New tag “${item.name}”`}
            />
          </div>
        );
      }
      const {result} = item;
      const key = resultKey(result);
      const leadingClass = 'text-muted-foreground size-4';
      return (
        <div ref={ref} key={key} className={highlight(isSelected)}>
          {result.kind === 'attachment' ? (
            <button
              type="button"
              onClick={() => handleSelectItem(item)}
              className="hover:bg-muted w-full cursor-pointer px-3 py-2.5 text-left text-sm"
            >
              {result.candidate.row}
            </button>
          ) : (
            <ScreenRow
              onClick={() => handleSelectItem(item)}
              leading={
                result.kind === 'tag' ? (
                  <Hash className={leadingClass} />
                ) : result.kind === 'fieldBrowse' ? (
                  <ListTree className={leadingClass} />
                ) : result.fieldType === FieldType.Text ? (
                  <Type className={leadingClass} />
                ) : (
                  <ListTree className={leadingClass} />
                )
              }
              label={
                result.kind === 'tag' || result.kind === 'fieldBrowse' ? (
                  result.kind === 'tag' ? result.name : result.fieldName
                ) : result.fieldType === FieldType.Text ? (
                  result.fieldName
                ) : (
                  <>
                    <span className="text-muted-foreground">{result.fieldName}:</span> {result.value}
                  </>
                )
              }
            />
          )}
        </div>
      );
    };
    // Rows in order, under a heading wherever the kind changes.
    const sectionOf = (item: SelectableItem) =>
      item.type === 'createTag'
        ? 'create'
        : item.result.kind === 'tag'
          ? 'Tags'
          : item.result.kind === 'attachment'
            ? 'Benefits'
            : 'Fields';
    const groups: Array<{label: string; rows: React.ReactNode[]}> = [];
    selectableItems.forEach((item, index) => {
      const label = sectionOf(item);
      const last = groups.at(-1);
      if (last?.label === label) last.rows.push(resultRow(item, index));
      else groups.push({label, rows: [resultRow(item, index)]});
    });
    return (
      <div className="flex flex-col gap-3">
        <TextInput
          ref={inputRef}
          autoFocus
          value={query}
          onChange={(value) => {
            setQuery(value);
            setIsOpen(true);
          }}
          onKeyDown={handleKeyDown}
          placeholder="Search tags and fields"
        />
        {(hasOwnPills || attachmentPills != null) && (
          <div className="flex flex-wrap items-center gap-1.5">{valuePills}</div>
        )}
        {attachmentBlocks}
        {groups.map((g) => (
          <React.Fragment key={g.label}>
            {g.label !== 'create' && <ScreenSection>{g.label}</ScreenSection>}
            <ScreenList>{g.rows}</ScreenList>
          </React.Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {/* The strip reads as one input: pills flow inline with the type-ahead,
          which claims the remaining width of the last line. Costs no second
          row until the pills wrap — the old stacked pills-then-input layout
          spent one on every phone. Clicking anywhere in the box focuses the
          input, like a text field. */}
      <div
        ref={containerRef}
        onClick={(e) => {
          // Only a click on the strip's own padding focuses the type-ahead —
          // clicks on pills (their remove buttons, the text-field pill's
          // inline editor) must keep their own focus.
          if (e.target === e.currentTarget) inputRef.current?.focus();
        }}
        className={cn(
          'dark:bg-input/30 border-input min-h-9 w-full cursor-text rounded-md border bg-transparent px-2 py-1 shadow-xs transition-[color,box-shadow]',
          'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
          'flex flex-wrap items-center gap-1',
        )}
      >
        {valuePills}
        <input
          ref={inputRef}
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          // A short prompt once pills occupy the box — the full one would
          // force an early wrap and imply the pills aren't part of the value
          placeholder={hasOwnPills ? 'Add…' : 'Add tags or fields...'}
          className="placeholder:text-muted-foreground min-w-24 flex-1 bg-transparent py-0.5 outline-none"
        />
      </div>

      {attachmentBlocks}

      <Popover
        anchorRef={containerRef}
        anchor="bottom-left"
        focusControlsRef={focusControlsRef}
        open={showPopover}
        onOutsideClick={() => setIsOpen(false)}
        onOutsideFocus={() => setIsOpen(false)}
      >
        <PopoverContents ref={popoverRef}>
          {selectableItems.map((item, index) => {
            const isSelected = selectedIndex === index;
            const ref = isSelected ? activeItemRef : null;

            if (item.type === 'result') {
              const {result} = item;
              return (
                <PopoverItem
                  key={resultKey(result)}
                  isSelected={isSelected}
                  ref={ref}
                  onClick={() => handleSelectItem(item)}
                  className="cursor-pointer text-sm"
                >
                  {result.kind === 'tag' ? (
                    <>
                      <span className="font-medium">{result.name}</span>
                      <span className="text-muted-foreground ml-1.5 text-xs">#tag</span>
                    </>
                  ) : result.kind === 'attachment' ? (
                    result.candidate.row
                  ) : result.kind === 'fieldBrowse' ? (
                    <span className="text-muted-foreground">{result.fieldName}:</span>
                  ) : result.fieldType === FieldType.Text ? (
                    <>
                      <span className="font-medium">{result.fieldName}</span>
                      <span className="text-muted-foreground ml-1.5 text-xs">(text)</span>
                    </>
                  ) : (
                    <>
                      <span className="text-muted-foreground">{result.fieldName}:</span>{' '}
                      <span className="font-medium">{result.value}</span>
                    </>
                  )}
                </PopoverItem>
              );
            } else {
              return (
                <PopoverItem
                  key="create-tag"
                  isSelected={isSelected}
                  ref={ref}
                  onClick={() => handleSelectItem(item)}
                  className="border-border cursor-pointer border-t text-sm"
                >
                  <span className="text-muted-foreground">Create tag</span>{' '}
                  <span className="font-medium">&ldquo;{item.name}&rdquo;</span>
                </PopoverItem>
              );
            }
          })}
        </PopoverContents>
      </Popover>
    </div>
  );
};
